import React, { useContext, useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getProgressOverview } from "../../redux/Slices/progressOverviewGraphSlice";
import { formatMeasurement, formatWeight } from "../../utils/unitConversion";
import { UnitContext } from "../../hooks/UnitProvider";
import FormSelect from "../../components/Common/FormSelect";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import Loader from "../../components/Common/Loader";

const AreaChartWithFilters = ({
  selectedClientId,
  isMessengerScreen = false,
}) => {
  const { trans } = useCustomTranslation();
  const dispatch = useDispatch();
  const { unit } = useContext(UnitContext);
  const { currentWeightUpdated } = useSelector(
    (state) => state.membershipOverview
  );
  // Get data from Redux store
  const { data, loading } = useSelector((state) => state.progress);
  const { clientCheckInData } = useSelector((state) => state.clientCheckIn);

  const [filter, setFilter] = useState("1m");
  const [selectedMetric, setSelectedMetric] = useState("bodyweight");
  const [selectedCircumference, setSelectedCircumference] = useState("waist");

  // Get target values from client data if available
  const getTargetValues = () => {
    if (selectedMetric === "bodyweight") {
      return clientCheckInData?.targetWeight
        ? {
            value: clientCheckInData.targetWeight,
            date: clientCheckInData.targetWeightdate
              ? new Date(clientCheckInData.targetWeightdate).getTime()
              : null,
            label: `Target Weight: ${formatWeight(
              unit,
              clientCheckInData.targetWeight
            )}`,
            show: true,
          }
        : { show: false };
    } else {
      return clientCheckInData?.targetCircumference
        ? {
            value: clientCheckInData.targetCircumference,
            label: `Target Circumference: ${formatMeasurement(
              unit,
              clientCheckInData.targetCircumference
            )}`,
            show: true,
          }
        : { show: false };
    }
  };

  const targetValues = getTargetValues();

  useEffect(() => {
    var component_name;
    if (selectedMetric === "bodyweight") {
      component_name = "current Weight";
    } else if (selectedMetric === "circumference") {
      if (selectedCircumference === "waist") component_name = "waist";
      else if (selectedCircumference === "chest") component_name = "chest";
      else if (selectedCircumference === "arm") component_name = "arm";
      else if (selectedCircumference === "shoulder")
        component_name = "shoulder";
      else if (selectedCircumference === "thigh") component_name = "thigh";
    }

    const payload = {
      ClientId: selectedClientId,
      ComponentName: component_name,
    };

    dispatch(getProgressOverview(payload));
  }, [dispatch, selectedMetric, selectedCircumference, selectedClientId]);

  // Process raw data from API
  const processData = (rawData) => {
    if (!rawData || !Array.isArray(rawData)) return [];

    return rawData
      .map((item) => ({
        ...item,
        date: new Date(item.date).getTime(),
      }))
      .sort((a, b) => a.date - b.date);
  };

  const processedData = {
    bodyweight: selectedMetric === "bodyweight" ? processData(data) : [],
    circumference: selectedMetric === "circumference" ? processData(data) : [],
  };

  // Enhanced date range calculation based on Excel logic
  const calculateDateRange = () => {
    const today = new Date();
    const currentData = processedData[selectedMetric];

    // Determine the effective start date based on client check-in data
    let effectiveStartDate;

    // Priority: firstWeightDate -> current weight date -> first data point -> target date
    if (clientCheckInData?.firstWeightDate) {
      effectiveStartDate = new Date(clientCheckInData.firstWeightDate);
    } else if (clientCheckInData?.weightDate) {
      effectiveStartDate = new Date(clientCheckInData.weightDate);
    } else if (currentData && currentData.length > 0) {
      effectiveStartDate = new Date(currentData[0].date);
    } else if (targetValues.show && targetValues.date) {
      effectiveStartDate = new Date(targetValues.date);
    } else {
      // No data available, use default ranges
      const standardRanges = {
        "1m": { months: 1, extensionDays: 60 },
        "3m": { months: 3, extensionDays: 30 },
        "6m": { months: 6, extensionDays: 60 },
        "1yr": { months: 12, extensionDays: 90 },
      };

      const range = standardRanges[filter];
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - range.months);

      const endDate = new Date();
      endDate.setDate(endDate.getDate() + range.extensionDays);

      return {
        minDate: startDate.getTime(),
        maxDate: endDate.getTime(),
      };
    }

    const todayTime = today.getTime();
    const startTime = effectiveStartDate.getTime();
    const daysDifference = Math.floor(
      (todayTime - startTime) / (1000 * 60 * 60 * 24)
    );

    let minDate = startTime;
    let maxDate = todayTime;

    // Apply Excel-based logic for each filter
    switch (filter) {
      case "1m":
        if (daysDifference < 30) {
          // If difference < 1 month, show: Start date + (60 - (Today-Start Day))
          const extensionDays = 60 - daysDifference;
          maxDate = new Date(
            todayTime + extensionDays * 24 * 60 * 60 * 1000
          ).getTime();
        } else if (daysDifference > 30) {
          // If difference > 30, show: Today + 60 days & -60 days
          minDate = new Date(todayTime - 30 * 24 * 60 * 60 * 1000).getTime();
          maxDate = new Date(todayTime + 30 * 24 * 60 * 60 * 1000).getTime();
        }
        break;

      case "3m":
        if (daysDifference < 90) {
          // < 3 months
          // Show: Start date + (120 - (Today-Start Day))
          const extensionDays = 120 - daysDifference;
          maxDate = new Date(
            todayTime + extensionDays * 24 * 60 * 60 * 1000
          ).getTime();
        } else if (daysDifference > 120) {
          // Show: Today +30 days & -120 days
          minDate = new Date(todayTime - 120 * 24 * 60 * 60 * 1000).getTime();
          maxDate = new Date(todayTime + 30 * 24 * 60 * 60 * 1000).getTime();
        }
        break;

      case "6m":
        if (daysDifference < 180) {
          // < 6 months
          // Show: Start date + (210 - (Today-Start Day))
          const extensionDays = 210 - daysDifference;
          maxDate = new Date(
            todayTime + extensionDays * 24 * 60 * 60 * 1000
          ).getTime();
        } else {
          // Show: Today +60 Days & -210 days
          minDate = new Date(todayTime - 210 * 24 * 60 * 60 * 1000).getTime();
          maxDate = new Date(todayTime + 60 * 24 * 60 * 60 * 1000).getTime();
        }
        break;

      case "1yr":
        if (daysDifference < 365) {
          // < 12 months
          // Show: Start date + (455 - (Today-Start Day))
          const extensionDays = 455 - daysDifference;
          maxDate = new Date(
            todayTime + extensionDays * 24 * 60 * 60 * 1000
          ).getTime();
        } else {
          // Show: Today + 90 days & -455 days
          minDate = new Date(todayTime - 455 * 24 * 60 * 60 * 1000).getTime();
          maxDate = new Date(todayTime + 90 * 24 * 60 * 60 * 1000).getTime();
        }
        break;

      default:
        // Default to 1 month logic
        if (daysDifference < 30) {
          const extensionDays = 60 - daysDifference;
          maxDate = new Date(
            todayTime + extensionDays * 24 * 60 * 60 * 1000
          ).getTime();
        } else if (daysDifference > 30) {
          minDate = new Date(todayTime - 60 * 24 * 60 * 60 * 1000).getTime();
          maxDate = new Date(todayTime + 60 * 24 * 60 * 60 * 1000).getTime();
        }
    }

    // Ensure minDate is not before the effective start date
    if (minDate < startTime) {
      minDate = startTime;
    }

    // Extend max date if target date is in the future
    if (targetValues.show && targetValues.date && targetValues.date > maxDate) {
      maxDate = targetValues.date;
    }

    return { minDate, maxDate };
  };

  // Filter data based on the calculated date range
  const getFilteredData = () => {
    const currentData = processedData[selectedMetric];
    if (!currentData || currentData.length === 0) return [];

    const { minDate, maxDate } = calculateDateRange();
    return currentData.filter(
      (item) => item.date >= minDate && item.date <= maxDate
    );
  };

  const filteredData = getFilteredData();
  const { minDate, maxDate } = calculateDateRange();

  // Prepare series data with first weight point if available
  // const prepareSeriesData = () => {
  //   let seriesData = filteredData.map((d) => ({
  //     x: d.date,
  //     y: d.value,
  //   }));

  //   // Add first weight point if it's not already included and we have the data
  //   if (selectedMetric === "bodyweight" && clientCheckInData?.firstWeight) {
  //     const firstWeightDate = clientCheckInData?.firstWeightDate
  //       ? new Date(clientCheckInData.firstWeightDate).getTime()
  //       : clientCheckInData?.weightDate
  //       ? new Date(clientCheckInData.weightDate).getTime()
  //       : null;

  //     if (firstWeightDate) {
  //       // Check if this date is already in our data
  //       const existingPoint = seriesData.find(
  //         (point) => Math.abs(point.x - firstWeightDate) < 24 * 60 * 60 * 1000 // within 1 day
  //       );

  //       if (!existingPoint) {
  //         // Add the first weight point
  //         seriesData.unshift({
  //           x: firstWeightDate,
  //           y: clientCheckInData.firstWeight,
  //         });

  //         // Sort the data by date
  //         seriesData.sort((a, b) => a.x - b.x);
  //       }
  //     }
  //   }

  //   return seriesData;
  // };
  const prepareSeriesData = () => {
    let seriesData = filteredData.map((d) => ({
      x: d.date,
      y: d.value,
    }));

    // Ensure there's a starting point at minDate
    if (seriesData.length > 0) {
      const { minDate } = calculateDateRange();
      const firstPoint = seriesData[0];

      if (firstPoint.x > minDate) {
        // Add synthetic point at minDate with firstPoint's value
        seriesData.unshift({
          x: minDate,
          y: firstPoint.y,
          synthetic: true, // (optional: mark for debugging)
        });
      }
    }

    // existing logic for firstWeight
    if (selectedMetric === "bodyweight" && clientCheckInData?.firstWeight) {
      const firstWeightDate = clientCheckInData?.firstWeightDate
        ? new Date(clientCheckInData.firstWeightDate).getTime()
        : clientCheckInData?.weightDate
        ? new Date(clientCheckInData.weightDate).getTime()
        : null;

      if (firstWeightDate) {
        const existingPoint = seriesData.find(
          (point) => Math.abs(point.x - firstWeightDate) < 24 * 60 * 60 * 1000
        );
        if (!existingPoint) {
          seriesData.unshift({
            x: firstWeightDate,
            y: clientCheckInData.firstWeight,
          });
          seriesData.sort((a, b) => a.x - b.x);
        }
      }
    }

    return seriesData;
  };

  const series = [
    {
      name: selectedMetric === "bodyweight" ? "Weight" : "Circumference",
      data: prepareSeriesData(),
    },
    ...(targetValues.show &&
    selectedMetric === "bodyweight" &&
    targetValues.date &&
    filteredData?.length > 0 &&
    targetValues?.date > filteredData[filteredData.length - 1]?.date
      ? [
          {
            name: "Target Weight",
            data: [
              {
                x: filteredData[filteredData.length - 1]?.date,
                y: filteredData[filteredData.length - 1]?.value,
              },
              {
                x: targetValues.date,
                y: targetValues.value,
              },
            ],
            color: "rgba(190, 197, 197, 0.65)",
            stroke: {
              width: 2,
              dashArray: 4,
            },
          },
        ]
      : []),
  ];

  // Chart options
  const options = {
    chart: {
      type: "area",
      zoom: { enabled: true },
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
    },
    markers: {
      size: 3,
      colors: ["#008FFB"],
      strokeWidth: 2,
      hover: {
        size: 4,
      },
    },
    legend: {
      show: false,
    },
    stroke: {
      width: 1,
      curve: "smooth",
    },
    xaxis: {
      type: "datetime",
      min: minDate,
      max: maxDate,
      tickAmount: 10, // Set number of ticks
      labels: {
        formatter: function (val) {
          const date = new Date(val);
          const month = date.toLocaleDateString("en-US", { month: "short" });
          const day = date.getDate();
          const year = date.getFullYear();

          // Show year if it's different from current year or if the range spans multiple years
          const currentYear = new Date().getFullYear();
          const minYear = new Date(minDate).getFullYear();
          const maxYear = new Date(maxDate).getFullYear();

          if (year !== currentYear || minYear !== maxYear) {
            return `${month} ${day}, ${year}`;
          }
          return `${month} ${day}`;
        },
        rotate: -45,
        rotateAlways: false,
      },
      axisBorder: {
        show: true,
        color: "#78909C",
        height: 1,
        width: "100%",
      },
      axisTicks: {
        show: false,
        color: "#78909C",
      },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter: (val) =>
          selectedMetric === "bodyweight"
            ? formatWeight(unit, val)
            : formatMeasurement(unit, val),
      },
    },
    tooltip: {
      x: {
        formatter: function (val) {
          return new Date(val).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });
        },
      },
      y: {
        formatter: (val) =>
          selectedMetric === "bodyweight"
            ? formatWeight(unit, val)
            : formatMeasurement(unit, val),
      },
    },
    colors: ["#008FFB"],
    dataLabels: { enabled: false },
    annotations: {
      yaxis: [],
      points:
        targetValues.show && targetValues.date
          ? [
              {
                x: targetValues.date,
                y: targetValues.value,
                marker: {
                  size: 6,
                  fillColor: "#36C1A8",
                  strokeColor: "$white",
                  strokeWidth: 1,
                  background: "#36c1a8",
                },
                label: {
                  borderColor: "#36C1A8",
                  offsetY: 0,
                  style: {
                    color: "$white",
                    background: "#36C1A8",
                  },
                },
              },
            ]
          : [],
    },
    grid: {
      borderColor: "#f1f1f1ff",
      strokeDashArray: 0,
      position: "back",
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
  };

  const totalWeightChange = () => {
    return formatWeight(
      unit,
      (clientCheckInData?.firstWeight ? clientCheckInData?.firstWeight : 0) -
        (currentWeightUpdated ? currentWeightUpdated : 0)
    );
  };

  const totalCircumferenceChange = () => {
    const circData = processedData.circumference;
    if (circData.length < 2) return 0;
    return circData[circData.length - 1].value - circData[0].value;
  };

  const handleOnChange = (event) => {
    setSelectedMetric(event.target.value);
  };
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div>
          <div
            className={`d-flex ${
              isMessengerScreen
                ? "flex-column"
                : "justify-content-between align-items-center"
            } mb-3`}
          >
            <div className="d-flex align-items-center justify-content-center mt-2">
              <FormSelect
                name="metric"
                value={selectedMetric}
                onChange={(e) => handleOnChange(e)}
                options={[
                  { value: "bodyweight", label: "Bodyweight" },
                  { value: "circumference", label: "Circumference" },
                ]}
              />
              {selectedMetric === "circumference" && (
                <FormSelect
                  name="circumference"
                  value={selectedCircumference}
                  onChange={(e) => setSelectedCircumference(e.target.value)}
                  options={[
                    { value: "chest", label: "Chest" },
                    { value: "waist", label: "Waist" },
                    { value: "arm", label: "Arm" },
                    { value: "shoulder", label: "Shoulder" },
                    { value: "thigh", label: "Thigh" },
                  ]}
                  className="ms-3"
                />
              )}
            </div>
            <div
              className={`${
                isMessengerScreen ? "d-flex justify-content-end mt-2" : ""
              }`}
            >
              <span
                className={`cursor-pointer me-2 ${
                  filter === "1m"
                    ? "membership-text-primary"
                    : "membership-text-muted"
                }`}
                onClick={() => setFilter("1m")}
              >
                1m
              </span>
              <span
                className={`cursor-pointer me-2 ${
                  filter === "3m"
                    ? "membership-text-primary"
                    : "membership-text-muted"
                }`}
                onClick={() => setFilter("3m")}
              >
                3m
              </span>
              <span
                className={`cursor-pointer me-2 ${
                  filter === "6m"
                    ? "membership-text-primary"
                    : "membership-text-muted"
                }`}
                onClick={() => setFilter("6m")}
              >
                6m
              </span>
              <span
                className={`cursor-pointer ${
                  filter === "1yr"
                    ? "membership-text-primary"
                    : "membership-text-muted"
                }`}
                onClick={() => setFilter("1yr")}
              >
                1yr
              </span>
            </div>
          </div>
          <Chart options={options} series={series} type="area" height={250} />
          <div
            className={
              isMessengerScreen
                ? "d-flex flex-column ms-1"
                : "d-flex justify-content-around"
            }
          >
            {isMessengerScreen ? (
              <>
                <Row className="bg-gray-100 rounded-3 p-1 mb-1">
                  <Col>
                    <p className="fs-6 font-bold mb-2">
                      {trans("Client.currentWeight")}
                    </p>
                    <p className="fs-6 font-normal mb-2">
                      {formatWeight(unit, currentWeightUpdated || 0)}
                    </p>
                  </Col>
                </Row>
                <Row className="bg-gray-100 rounded-3 p-1 mb-1">
                  <Col>
                    <p className="fs-6 font-bold mb-2">
                      {trans("Client.totalWeightChange")}
                    </p>
                    <p className="fs-6 font-normal mb-2">
                      {totalWeightChange()}
                    </p>
                  </Col>
                </Row>
                <Row className="bg-gray-100 rounded-3 p-1 mb-1">
                  <Col>
                    <p className="fs-6 font-bold mb-2">
                      {trans("Client.totalCircumferenceChange")}
                    </p>
                    <p className="fs-6 font-normal mb-2">
                      {formatMeasurement(unit, totalCircumferenceChange())}
                    </p>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <div>
                  <span className="d-block membership-text-muted fs-6">
                    {trans("Client.currentWeight")}
                  </span>
                  <span className="fs-6">
                    {formatWeight(unit, currentWeightUpdated || 0)}
                  </span>
                </div>
                <div>
                  <span className="d-block membership-text-muted fs-6">
                    {trans("Client.totalWeightChange")}
                  </span>
                  <span className="fs-6">{totalWeightChange()}</span>
                </div>
                <div>
                  <span className="d-block membership-text-muted fs-6">
                    {trans("Client.totalCircumferenceChange")}
                  </span>
                  <span className="fs-6 pt-0 mt-0">
                    {formatMeasurement(unit, totalCircumferenceChange())}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AreaChartWithFilters;